import { useState } from 'react';

interface Props {
  transcript: string;
  audioFileName: string;
}

export function TranscriptionPanel({ transcript, audioFileName }: Props) {
  const [copied, setCopied] = useState(false);

  const lines = transcript.split('\n');
  const wordCount = transcript.trim().split(/\s+/).filter(Boolean).length;
  const charCount = transcript.length;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  const onDownload = () => {
    const blob = new Blob([transcript], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${audioFileName.replace(/\.[^/.]+$/, '')}-transcripcion.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="reveal-up" style={{ animationDelay: '120ms' }}>
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
        <p className="label-mono">
          <span className="text-[var(--color-amber-mid)]">[04]</span> · stdout
          <span className="text-[var(--color-asphalt-300)]"> // transcript.txt</span>
        </p>
        <div className="flex items-center gap-2 no-print">
          <ActionButton onClick={onCopy} label={copied ? '✓ copied' : 'cp $clipboard'} />
          <ActionButton onClick={onDownload} label="wget .txt" />
        </div>
      </div>

      <div className="term-box">
        <div className="flex items-center justify-between px-5 py-3 term-header">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em]">
            <span className="text-[var(--color-burgundy)]">●</span>
            <span className="text-[var(--color-amber-mid)]">●</span>
            <span className="text-[var(--color-asphalt)]">●</span>
            <span className="ml-3 text-[var(--color-asphalt-500)]">/var/log/transcript.log</span>
          </div>
          <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-asphalt-500)]">
            <span>
              <span className="nums-tabular text-[var(--color-asphalt)]">{lines.length}</span>L
            </span>
            <span>
              <span className="nums-tabular text-[var(--color-asphalt)]">{wordCount}</span>W
            </span>
            <span>
              <span className="nums-tabular text-[var(--color-asphalt)]">{charCount}</span>C
            </span>
          </div>
        </div>

        <div className="px-5 py-6 max-h-[60vh] overflow-y-auto relative z-10">
          <div className="whitespace-pre-wrap font-mono text-[14px] leading-[1.7] text-[var(--color-asphalt)]">
            {lines.map((line, i) => (
              <div key={i} className="flex gap-5 group">
                <span
                  className="font-mono text-[11px] text-[var(--color-asphalt-400)] pt-[5px] select-none nums-tabular shrink-0 w-8 text-right"
                  aria-hidden
                >
                  {String(i + 1).padStart(3, '0')}
                </span>
                <span className="flex-1 min-w-0 break-anywhere">{line || ' '}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ActionButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className="term-btn">
      {label}
    </button>
  );
}
